import colors from 'shared/colors';
import useChartValueFormatter from '@/composables/grid-page/chart/chart-value-formatter';
import useGoalSettings from '@/composables/logged-in-user-account/goal-settings';
import { AREA, DASH, DATETIME, DOT, LINE } from '@/lib/constants';
import { DateTime } from 'luxon';
import { computed, ref } from 'vue';
import { formatTooltipCard, syncReferenceLinesToAxis } from '@/composables/grid-page/chart/utils';
import { linearRegression } from '@/lib/charts/line-chart';
import { rgbaToHex } from 'shared/lib/color';
import { useI18n } from 'vue-i18n';

export default function useStatusScoreChart(result, chart = ref({})) {
  const { t } = useI18n();
  const { goalSettings } = useGoalSettings();
  const { formatValue } = useChartValueFormatter(chart);

  const hasData = computed(() => result.value?.statusScoreData?.length > 0);

  const startDate = computed(() => {
    if (!result.value?.startDate) {
      return DateTime.local();
    }
    return result.value.startDate;
  });

  const endDate = computed(() => {
    if (!result.value?.endDate) {
      return DateTime.local();
    }
    return result.value.endDate;
  });

  const startMs = computed(() => startDate.value?.toMillis() ?? 0);
  const endMs = computed(() => endDate.value?.toMillis() ?? 0);

  const scoreData = computed(() => {
    if (!hasData.value) {
      return [];
    }
    return result.value.statusScoreData
      .filter((e) => e.x >= startMs.value && e.x <= endMs.value)
      .sort((a, b) => (a.x <= b.x ? -1 : 1));
  });

  const trendData = computed(() => {
    if (scoreData.value.length < 2) {
      return [];
    }
    const { slope, intercept } = linearRegression(scoreData.value);
    return [
      { x: startMs.value, y: intercept + (slope * startMs.value) },
      { x: endMs.value, y: intercept + (slope * endMs.value) },
    ];
  });

  const redArea = computed(() => {
    const threshold = goalSettings.value.redThreshold / 10;
    return [
      { x: startMs.value, y: threshold },
      { x: endMs.value, y: threshold },
    ];
  });

  const yellowArea = computed(() => {
    const threshold = (goalSettings.value.yellowThreshold - goalSettings.value.redThreshold) / 10;
    return [
      { x: startMs.value, y: threshold },
      { x: endMs.value, y: threshold },
    ];
  });

  const greenArea = computed(() => {
    const threshold = 100 - goalSettings.value.yellowThreshold / 10;
    return [
      { x: startMs.value, y: threshold },
      { x: endMs.value, y: threshold },
    ];
  });

  const chartOptions = computed(() => ({
    chart: {
      animation: false,
      events: {
        render() {
          syncReferenceLinesToAxis(this, chart.value.referenceLines);
        },
      },
    },
    legend: {
      enabled: true,
      align: 'left',
      verticalAlign: 'top',
      x: 50,
      floating: true,
    },
    plotOptions: {
      area: {
        stacking: 'percent',
        lineWidth: 0,
      },
    },
    series: [
      {
        states: { inactive: { opacity: 1 } },
        type: AREA,
        data: greenArea.value,
        color: colors.green.base,
        fillOpacity: 0.2,
        marker: { enabled: false },
        enableMouseTracking: false,
        showInLegend: false,
      },
      {
        states: { inactive: { opacity: 1 } },
        type: AREA,
        data: yellowArea.value,
        color: colors.yellow.base,
        fillOpacity: 0.2,
        marker: { enabled: false },
        enableMouseTracking: false,
        showInLegend: false,
      },
      {
        states: { inactive: { opacity: 1 } },
        type: AREA,
        data: redArea.value,
        color: colors.red.base,
        fillOpacity: 0.2,
        marker: { enabled: false },
        enableMouseTracking: false,
        showInLegend: false,
      },
      ...(trendData.value.length > 0 ? [{
        states: { inactive: { opacity: 1 } },
        type: LINE,
        dashStyle: DASH,
        data: trendData.value,
        color: colors.grey.lighten3,
        marker: { enabled: false },
        enableMouseTracking: false,
        name: t('highcharts.trend'),
      }] : []),
      {
        type: LINE,
        data: scoreData.value,
        clip: false,
        color: rgbaToHex(colors.grey.darken2),
        connectNulls: true,
        showInLegend: false,
      },
    ],
    time: { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone },
    xAxis: {
      lineColor: rgbaToHex(colors.grey.lighten3),
      lineWidth: 1,
      type: DATETIME,
      minTickInterval: 28 * 24 * 3600 * 1000,
      minRange: 28 * 24 * 3600 * 1000,
      min: startMs.value,
      max: endMs.value,
    },
    yAxis: {
      min: 0,
      max: 10,
      lineWidth: 0,
      gridLineColor: rgbaToHex(colors.grey.lighten3),
      gridLineDashStyle: DOT,
      title: { text: '' },
    },
    tooltip: {
      shared: true,
      useHTML: true,
      formatter() {
        const point = this.points?.[this.points.length - 1]?.point;
        if (!point) {
          return false;
        }
        const tag = `background:${colors.grey.lighten5};border-radius:4px;padding:1px 3px`;
        const row = 'display:flex;align-items:center;gap:.6rem';
        const date = `<div style="margin-bottom:6px;color:${colors.grey.darken4}">${DateTime.fromMillis(point.x).toFormat('LLL d, yyyy')}</div>`;
        const actual = `<div style="${row}"><span style="${tag}">${formatValue(point.y)}</span><span style="color:${colors.grey.lighten1};margin-left:auto">${t('highcharts.actual')}</span></div>`;
        return formatTooltipCard(date + actual);
      },
    },
  }));

  return {
    chartOptions,
    hasData,
  };
}
